import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, FormInstance, Input, InputNumber, message, Modal, Tooltip, type DatePickerProps, type TableColumnsType, type TableProps } from "antd";
import axios from "axios";
import dayjs from 'dayjs';
import { debounce, isEqual } from "lodash";
import { useAuth } from "../context/AuthContext";
function StartLicense(stimulsoftInstance: any) {
    const key = process.env.REACT_APP_STIMULSOFT_LICENSE_KEY;
    if (key && key.trim() !== "") {
        try {
            stimulsoftInstance.Base.StiLicense.key = key.trim();
        } catch (error: any) { }
    } else {
        const hardcodedKey = "6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHmKmIYtQbGVffUafn4grxXukjNz+mLI/d2lO8A9DuHxAxu4MmrN4lqhtklB0Pcnfq+AIk+1s6kU0D6JIIxpfQs1FV6pzwrzAWDLexRPvVgpswR/C5n69gFjFmWmtV0bGCTAj6H/MHgwElWYT9nYccmuFH0sfAj7VELa2HehqREJ7di+05AWeWIScfcWsf0XZ3JK+2X3reYvhUEAxrCwX3FDw2p1hPQIfiUVkUd4NRvfwWBLOjP2ZTfp9dzNe1D6T7miK1VBd731o/ex6q1bkdNH+G1KV6OLxdQwb9zWr4AR0QtMKwrphskgAYb5PMCectoVCeRFKtWSm8qnMP+GN8v0GHbJGQ/ZYWMAsE94nCEhnJz9T/xfy1b6WEWqpUUjVsfPzm/naRz0bxVN+AtFW7thTBMwGXoKTJXnKhfrI5u/fRffUu78ejnazBjWCHANN/xF7XOLhnFWw5JZHPXkaoCbiG8kBL3bgL4DtSyZ7jecAPmJUtainwLNMdo75J6hvu/pS+jDUs2tqRjEv6PTehXg";
        try {
            stimulsoftInstance.Base.StiLicense.key = hardcodedKey;
        } catch (fallbackError: any) { }
    }
}

export interface DataType {
    key: React.Key;
    item_code: string;
    item_name: string;
    uom: string;
    qty: number;
    price: number;
    disc_percent: number;
    disc_amount: number;
    total: number;
    return_qty: number;
}

type AutosaveStatus = 'stopped' | 'idle' | 'processing' | 'error';
export interface SalesOrderInvoiceReturnProps {
    transformStyle: React.CSSProperties;
    onDateChange: DatePickerProps['onChange'];
    columns: TableColumnsType<DataType>;
    rowSelection: TableProps<DataType>["rowSelection"];
    form: FormInstance;
    isModalOpen: boolean;
    showModal: () => void;
    handleCancel: () => void;
    handleRowDoubleClick: (record: any) => void;
    isAddLineModalOpen: boolean;
    showAddLineModal: () => void;
    handleAddLineCancel: () => void;
    currentInvoiceNo: string;
    dataSource: DataType[];
    handleImport: (selectedItems: any[]) => void;
    handleDelete: () => void;
    selectedRowKeys: React.Key[];
    autosaveStatus: AutosaveStatus;
    handleSubmit: () => void;
    isSubmitDisabled: boolean;
    handleVoid: () => void;
    isVoidDisabled: boolean;
    isInvoiceSelected: boolean;
    handlePrint: () => void;
    isPrintDisabled: boolean;
    isReportModalOpen: boolean;
    handleReportModalCancel: () => void;
}

const terbilang = (n: number): string => {
    if (n < 0) return "minus " + terbilang(Math.abs(n));

    const bilangan = [
        '', 'satu', 'dua', 'tiga', 'empat', 'lima', 'enam', 'tujuh', 'delapan', 'sembilan', 'sepuluh', 'sebelas'
    ];

    let result = '';
    if (n < 12) {
        result = bilangan[n];
    } else if (n < 20) {
        result = terbilang(n - 10) + ' belas';
    } else if (n < 100) {
        result = terbilang(Math.floor(n / 10)) + ' puluh ' + terbilang(n % 10);
    } else if (n < 200) {
        result = 'seratus ' + terbilang(n - 100);
    } else if (n < 1000) {
        result = terbilang(Math.floor(n / 100)) + ' ratus ' + terbilang(n % 100);
    } else if (n < 2000) {
        result = 'seribu ' + terbilang(n - 1000);
    } else if (n < 1000000) {
        result = terbilang(Math.floor(n / 1000)) + ' ribu ' + terbilang(n % 1000);
    } else if (n < 1000000000) {
        result = terbilang(Math.floor(n / 1000000)) + ' juta ' + terbilang(n % 1000000);
    } else if (n < 1000000000000) {
        result = terbilang(Math.floor(n / 1000000000)) + ' milyar ' + terbilang(n % 1000000000);
    } else if (n < 1000000000000000) {
        result = terbilang(Math.floor(n / 1000000000000)) + ' trilyun ' + terbilang(n % 1000000000000);
    }
    return result.trim();
};

const withSalesOrderInvoiceReturnLogic = <P extends SalesOrderInvoiceReturnProps>(
    WrappedComponent: React.ComponentType<P>
) => {
    const EnhancedComponent: React.FC<Omit<P, keyof SalesOrderInvoiceReturnProps>> = (props) => {
        const { userData } = useAuth();
        const [form] = Form.useForm();
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isAddLineModalOpen, setIsAddLineModalOpen] = useState(false);
        const [currentInvoiceNo, setCurrentInvoiceNo] = useState<string>("");
        const [dataSource, setDataSource] = useState<DataType[]>([]);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [ppnValue, setPpnValue] = useState<number>(0);
        const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('stopped');
        const [lastSavedFormValues, setLastSavedFormValues] = useState<any>(null);
        const [lastSavedTableData, setLastSavedTableData] = useState<DataType[]>([]);
        const formValues = Form.useWatch([], form);
        const [originalBrandCode, setOriginalBrandCode] = useState<string | null>(null);
        const [originalWarehouseCode, setOriginalWarehouseCode] = useState<string | null>(null);
        const autosaveStatusRef = useRef(autosaveStatus);
        const [isInvoiceSelected, setIsInvoiceSelected] = useState<boolean>(false);
        const originalBrandCodeRef = useRef<string | null>(originalBrandCode);
        const originalWarehouseCodeRef = useRef<string | null>(originalWarehouseCode);
        const [isReportModalOpen, setIsReportModalOpen] = useState(false);
        const handleReportModalCancel = () => {
            setIsReportModalOpen(false);
        };

        useEffect(() => {
            autosaveStatusRef.current = autosaveStatus;
        }, [autosaveStatus]);

        useEffect(() => {
            originalBrandCodeRef.current = originalBrandCode;
            originalWarehouseCodeRef.current = originalWarehouseCode;
        }, [originalBrandCode, originalWarehouseCode]);

        useEffect(() => {
            const loadDataFromStorage = async () => {
                const storedData = localStorage.getItem('selectedInvoiceReturnData');
                if (storedData) {
                    try {
                        const data = JSON.parse(storedData);
                        const { header, details } = data;

                        let brandDisplayValue = header.brand;
                        try {
                            if (header.brand) {
                                const brandResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/brand-description/${header.brand}`);
                                if (brandResponse.data && brandResponse.data.description) {
                                    brandDisplayValue = brandResponse.data.description;
                                }
                            }
                        } catch (brandError) {
                            console.error("Could not fetch brand description, using code instead:", brandError);
                        }

                        let warehouseDisplayValue = header.warehouse;
                        try {
                            if (header.warehouse) {
                                const warehouseResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse-name/${header.warehouse}`);
                                if (warehouseResponse.data && warehouseResponse.data.warehouse_name) {
                                    warehouseDisplayValue = warehouseResponse.data.warehouse_name;
                                }
                            }
                        } catch (warehouseError) {
                            console.error("Could not fetch warehouse name, using code instead:", warehouseError);
                        }

                        const statusText = header.status === '1' ? 'Draft' : header.status === '2' ? 'Approved' : header.status === '3' ? 'Waiting for Approved' : 'Unknown';

                        const formValuesToSet = {
                            ...header,
                            brand: brandDisplayValue,
                            warehouse: warehouseDisplayValue,
                            status: statusText,
                            tax_invoice_return_date: header.tax_invoice_return_date
                                ? dayjs(header.tax_invoice_return_date, ['DD/MM/YYYY', 'DD/MM/YYYY', 'DD/MM/YYYY'])
                                : null,
                        };



                        form.setFieldsValue(formValuesToSet);
                        setDataSource(details || []);
                        setOriginalBrandCode(header.brand);
                        setOriginalWarehouseCode(header.warehouse);
                        setLastSavedFormValues(formValuesToSet);
                        setLastSavedTableData(details || []);
                        setIsInvoiceSelected(true);

                        if (statusText === 'Draft') {
                            setAutosaveStatus('idle');
                        } else {
                            setAutosaveStatus('stopped');
                        }

                        localStorage.removeItem('selectedInvoiceReturnData');
                    } catch (error) {
                        console.error("Failed to parse or load data from local storage", error);
                        message.error("Gagal memuat data dari penyimpanan lokal.");
                        form.setFieldsValue({
                            status: 'Draft',
                            invoice_return_date: dayjs().format('DD/MM/YYYY'),
                        });
                    }
                } else {
                    form.setFieldsValue({
                        status: 'Draft',
                        invoice_return_date: dayjs().format('DD/MM/YYYY'),
                    });
                }
            };

            loadDataFromStorage();
        }, [form]);

        useEffect(() => {
            const fetchPpnValue = async () => {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ppn`);
                    let fetchedPpn = 0;


                    if (typeof response.data === 'object' && response.data !== null && response.data.value_ppn !== undefined) {
                        fetchedPpn = Number(response.data.value_ppn);
                    }

                    else if (!isNaN(Number(response.data))) {
                        fetchedPpn = Number(response.data);
                    }
                    setPpnValue(isNaN(fetchedPpn) ? 0 : fetchedPpn);

                } catch (error) {
                    console.error("Error fetching PPN value:", error);
                    setPpnValue(0);
                    message.error("Gagal mengambil nilai PPN dari server.");
                }
            };

            fetchPpnValue();
        }, []);

        useEffect(() => {

            const calculateTotals = () => {
                let subTotal = 0;
                let discount = 0;
                const hasReturnedItems = dataSource.length > 0;

                dataSource.forEach(item => {
                    const price = Number(item.price) || 0;
                    const returnQty = Number(item.return_qty) || 0;
                    const unitDisc = item.qty > 0 ? (Number(item.disc_amount) / item.qty) : 0;
                    if (returnQty > 0) {
                        subTotal += (price * returnQty);
                        discount += (unitDisc * returnQty);
                    }
                });

                const total = subTotal - discount;
                let vat = 0;

                if (hasReturnedItems && ppnValue > 0) {
                    vat = (total * ppnValue) / 100;
                }
                const invoiceReturnAmount = total + vat;

                form.setFieldsValue({
                    sub_total: subTotal,
                    discount: discount,
                    total: total,
                    vat: vat,
                    invoice_return_amount: invoiceReturnAmount,
                });
            };

            calculateTotals();
        }, [dataSource, ppnValue, form]);

        const saveData = useCallback(debounce(async (currentFormValues: any, currentTableData: DataType[]) => {
            const invoiceReturnNo = currentFormValues.invoice_return_no;

            if (
                !invoiceReturnNo ||
                autosaveStatusRef.current === 'stopped' ||
                !invoiceReturnNo.startsWith('AMP-SOIR') ||
                (isEqual(currentFormValues, lastSavedFormValues) &&
                    isEqual(currentTableData, lastSavedTableData))
            ) {
                if (autosaveStatusRef.current !== 'stopped') {
                    setAutosaveStatus('idle');
                }
                return;
            }

            setAutosaveStatus('processing');
            try {
                const formVals = { ...currentFormValues };
                if (originalBrandCodeRef.current) {
                    formVals.brand = originalBrandCodeRef.current;
                }
                if (originalWarehouseCodeRef.current) {
                    formVals.warehouse = originalWarehouseCodeRef.current;
                }

                const payload = {
                    header: {
                        ...formVals,
                        status: formVals.status === 'Draft' ? 1 : (formVals.status || 1),
                        tax_invoice_return_date: formVals.tax_invoice_return_date,
                    },
                    details: currentTableData,
                };

                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/save-invoice-return`, payload);

                setAutosaveStatus('idle');
                setLastSavedFormValues(currentFormValues);
                setLastSavedTableData(currentTableData);
            } catch (error) {
                console.error("Autosave failed:", error);
                setAutosaveStatus('error');
                message.error('Autosave gagal. Periksa koneksi Anda.');
            }
        }, 2000), [lastSavedFormValues, lastSavedTableData]);

        useEffect(() => {
            const invoiceReturnNo = form.getFieldValue('invoice_return_no');
            const status = form.getFieldValue('status');
            const isDraft = status === 'Draft';

            if (formValues && invoiceReturnNo && isDraft) {
                saveData(formValues, dataSource);
            } else if (!invoiceReturnNo || !isDraft) {
                setAutosaveStatus('stopped');
            }
        }, [form, formValues, dataSource, saveData]);

        const handleReturnQtyChange = (value: number, key: React.Key) => {
            setDataSource(prev => prev.map(item => {
                if (item.key === key) {
                    const newReturnQty = Math.min(value, item.qty);
                    const unitPrice = item.price || 0;
                    const unitDisc = item.qty > 0 ? (item.disc_amount / item.qty) : 0;
                    return {
                        ...item,
                        return_qty: newReturnQty,
                        total: (newReturnQty * unitPrice) - (newReturnQty * unitDisc)
                    };
                }
                return item;
            }));
        };

        const showModal = () => {
            setIsModalOpen(true);
        };

        const handleCancel = () => {
            setIsModalOpen(false);
        };

        const showAddLineModal = () => {
            const invoiceNo = form.getFieldValue('invoice_no');
            if (invoiceNo) {
                setCurrentInvoiceNo(invoiceNo);
                setIsAddLineModalOpen(true);
            } else {
                message.warning('Silakan pilih invoice terlebih dahulu untuk menambah item.');
            }
        };

        const handleAddLineCancel = () => {
            setIsAddLineModalOpen(false);
        };

        const handleImport = (selectedItems: any[]) => {
            const newItems = selectedItems.map(item => ({
                ...item,
                total: (item.qty * item.price) - item.disc_amount,
                return_qty: item.qty,
            }));
            setDataSource(prevData => [...prevData, ...newItems]);
            handleAddLineCancel();
            message.success(`${selectedItems.length} item(s) imported successfully.`);
        };

        const handleDelete = () => {
            if (selectedRowKeys.length === 0) {
                message.warning("Tidak ada data yang dipilih untuk dihapus.");
                return;
            }
            const newDataSource = dataSource.filter(item => !selectedRowKeys.includes(item.key));
            setDataSource(newDataSource);
            setSelectedRowKeys([]);
            message.success(`${selectedRowKeys.length} item(s) deleted successfully.`);
        };

        const handleSubmit = async () => {
            const invoiceReturnNo = form.getFieldValue('invoice_return_no');
            const invoiceNo = form.getFieldValue('invoice_no')
            if (!invoiceReturnNo) {
                message.error("Nomor Invoice Return tidak ditemukan untuk disubmit.");
                return;
            }

            try {
                await form.validateFields();

                if (dataSource.length === 0) {
                    message.error("Silakan tambah item terlebih dahulu.");
                    return;
                }

                const hasInvalidReturn = dataSource.some(item => !item.return_qty || item.return_qty <= 0);
                if (hasInvalidReturn) {
                    message.error("Kolom 'Return' masih 0 atau tidak lebih dari 0, wajib di isi!");
                    return;
                }

                Modal.confirm({
                    title: 'Konfirmasi Submit',
                    content: `Apakah Anda yakin ingin mensubmit invoice return (${invoiceReturnNo})?`,
                    okText: 'Ya, Submit',
                    cancelText: 'Tidak',
                    onOk: () => {
                        showRemarkModal(invoiceNo, invoiceReturnNo);
                    },
                });
            } catch (errorInfo) {
                console.error("Validation Failed:", errorInfo);
            }
        };

        const showRemarkModal = (invoiceNo: string, invoiceReturnNo: string) => {
            const prefix = `Pembatalan invoice ${invoiceNo} `;
            let remarkValue = prefix;
            Modal.confirm({
                title: 'Remark Approval',
                content: (
                    <Input.TextArea
                        rows={3}
                        defaultValue={prefix}
                        onChange={(e) => {
                            if (!e.target.value.startsWith(prefix)) {
                                e.target.value = prefix;
                            }
                            remarkValue = e.target.value;
                        }}
                    />
                ),
                onOk: async () => {
                    const key = 'submitting';
                    message.loading({ content: 'Memproses approval...', key });
                    try {
                        const payloadApproval = {
                            remark: remarkValue,
                            code: '04',
                            return_sales_order: '1',
                            no_document: invoiceReturnNo,
                            amount: form.getFieldValue('total'),
                            warehouse: originalWarehouseCodeRef.current,
                            date_request: new Date().toISOString(),
                            request_by: userData?.kode_grup1
                        };
                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/approvals/submit-return-so`, payloadApproval);
                        message.success({ content: 'Berhasil dikirim ke approval.', key });
                        form.setFieldsValue({ status: 'Waiting for Approved' });
                    } catch (error) {
                        message.error({ content: 'Gagal mengirim approval.', key });
                    }
                }
            });
        };

        const handleVoid = () => {
            const invoiceReturnNo = form.getFieldValue('invoice_return_no');
            if (!invoiceReturnNo) {
                message.error("Nomor Invoice Return tidak ditemukan untuk dibatalkan.");
                return;
            }

            Modal.confirm({
                title: 'Konfirmasi Pembatalan',
                content: `Apakah anda yakin membatalkan invoice return tersebut (${invoiceReturnNo})?`,
                okText: 'Ya, Batalkan',
                cancelText: 'Tidak',
                onOk: async () => {
                    const key = 'voiding';
                    message.loading({ content: 'Membatalkan invoice return...', key });
                    try {
                        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/sales-order-invoice-return/${invoiceReturnNo}`);
                        message.success({ content: 'Invoice return berhasil dibatalkan.', key, duration: 2 });

                        form.resetFields();
                        setDataSource([]);
                        setLastSavedFormValues(null);
                        setLastSavedTableData([]);
                        setAutosaveStatus('stopped');
                        setOriginalBrandCode(null);
                        setOriginalWarehouseCode(null);
                        setIsInvoiceSelected(false);
                        form.setFieldsValue({
                            status: 'Draft',
                            invoice_return_date: dayjs().format('DD/MM/YYYY'),
                        });
                    } catch (error) {
                        console.error("Gagal membatalkan invoice return:", error);
                        message.error({ content: 'Gagal membatalkan invoice return. Silakan coba lagi.', key, duration: 2 });
                    }
                },
            });
        };

        const handlePrint = async () => {
            const invoiceReturnNo = form.getFieldValue('invoice_return_no');
            if (!invoiceReturnNo) {
                message.warning('Silakan pilih atau buat invoice return terlebih dahulu untuk mencetak.');
                return;
            }

            const key = 'printing';
            message.loading({ content: 'Mempersiapkan data cetak...', key });

            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/print-data/${invoiceReturnNo}`);
                const { header, details, ppn } = response.data;

                const formatDate = (dateStr: string | null) => dateStr ? dayjs(dateStr).format('DD/MM/YYYY') : '';
                const monthNamesID = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
                const formatIndonesianDate = (dateStr: string | null) => {
                    if (!dateStr) return '';
                    const parts = String(dateStr).split('/');
                    if (parts.length !== 3) return dateStr;
                    const dd = parseInt(parts[0], 10);
                    const mm = parseInt(parts[1], 10);
                    const yyyy = parts[2].trim();
                    if (isNaN(dd) || isNaN(mm) || mm < 1 || mm > 12) return dateStr;
                    return `${dd} ${monthNamesID[mm - 1]} ${yyyy}`;
                };
                const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
                const amountInWords = capitalize(terbilang(Math.round(header.invoice_return_amount || 0))) + ' Rupiah';
                const warehouseMapping: { [key: string]: string } = {
                    WH_AMP_PBUN: 'Pangkalanbun',
                    WH_AMP_PKY: 'Palangkaraya',
                    WH_AMP_SPT: 'Sampit',
                    WH_AMP_TWH: 'Teweh',
                };
                const warehouseDisplay = header.warehouse && warehouseMapping[header.warehouse] ? warehouseMapping[header.warehouse] : null;
                const reportPayload = {
                    Header: [{
                        invoice_return_no: header.invoice_return_no || "",
                        invoice_return_date: formatIndonesianDate(header.invoice_return_date),
                        invoice_no: header.invoice_no || "",
                        invoice_date: formatIndonesianDate(header.invoice_date),
                        sales_order_no: header.sales_order_no || "",
                        salesman: header.salesman || "",
                        packing_list_no: header.packing_list_no || "",
                        brand: header.brand || "",
                        profit_center: header.profit_center || "",
                        warehouse: warehouseDisplay,
                        transaction_type: header.transaction_type || "",
                        event_no: header.event_no || "",
                        customer_name: header.customer_name || "",
                        bill_to: header.bill_to || "",
                        address: header.address || "",
                        phone_no: header.phone_no || "",
                        npwp: header.npwp || "",
                        tax_invoice_return_no: header.tax_invoice_return_no || "",
                        tax_invoice_return_date: formatDate(header.tax_invoice_return_date),
                        remark: header.remark || "",
                        sub_total: Number(header.sub_total) || 0,
                        discount: Number(header.discount) || 0,
                        total: Number(header.total) || 0,
                        vat: Number(header.vat) || 0,
                        invoice_return_amount: Number(header.invoice_return_amount) || 0,
                        date_now: dayjs().format('DD/MM/YYYY'),
                        terbilang: amountInWords
                    }],
                    Details: details.map((item: any, index: number) => ({
                        row_no: index + 1,
                        item_code: item.item_no || "",
                        item_name: item.item_name || "",
                        qty: Number(item.qty) || 0,
                        uom: item.uom || "",
                        price: Number(item.price) || 0,
                        disc_percent: Number(item.disc_percent) || 0,
                        discount: Number(item.disc_amount) || 0,
                        total: Number(item.total) || 0
                    }))
                };
                await import("stimulsoft-reports-js/Css/stimulsoft.viewer.office2013.whiteblue.css"); 
                await import("stimulsoft-reports-js/Scripts/stimulsoft.reports"); 
                const viewerModule = await import("stimulsoft-reports-js/Scripts/stimulsoft.viewer"); 
                const Stimulsoft = viewerModule.Stimulsoft || (window as any).Stimulsoft; 
                StartLicense(Stimulsoft);
                setIsReportModalOpen(true);
                setTimeout(() => {
                    const report = Stimulsoft.Report.StiReport.createNewReport();
                    report.loadFile("/reports/SOIVR/sales_order_invoice_return.mrt");
                    const dataSet = new Stimulsoft.System.Data.DataSet("ReportData");
                    dataSet.readJson(reportPayload);
                    report.regData(dataSet.dataSetName, "", dataSet);
                    report.dictionary.synchronize();
                    const options = new Stimulsoft.Viewer.StiViewerOptions();
                    options.toolbar.showOpenButton = true;
                    const viewer = new Stimulsoft.Viewer.StiViewer(options, "StiViewer", false);
                    (viewer as any).onOpenReport = (args: any) => {
                        const newReport = args.report;
                        if (newReport) {
                            newReport.regData(dataSet.dataSetName, "", dataSet);
                            newReport.dictionary.synchronize();
                            viewer.report = newReport;
                        }
                    };
                    viewer.report = report;
                    viewer.renderHtml("sti-viewer-container");
                    message.success({ content: 'Dokumen siap dicetak!', key, duration: 2 });
                }, 300);

            } catch (error: any) {
                console.error("Gagal mempersiapkan data cetak:", error);
                message.error({ content: error.message || 'Gagal mempersiapkan data cetak. Silakan coba lagi.', key, duration: 3 });
            }
        };

        const proceedWithInvoiceSelection = async (record: any) => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/generate-invoice-return-no`);
                const newInvoiceReturnNo = response.data.invoice_return_no;
                setOriginalBrandCode(record.brand);
                let brandDisplayValue = record.brand;
                try {
                    if (record.brand) {
                        const brandResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/brand-description/${record.brand}`);
                        if (brandResponse.data && brandResponse.data.description) {
                            brandDisplayValue = brandResponse.data.description;
                        }
                    }
                } catch (brandError) {
                    console.error("Tidak dapat mengambil deskripsi brand, menggunakan kode sebagai gantinya:", brandError);
                }
                const displayEventNo = record.event_no === '01' ? '3rd Invoice Part & Acc' : record.event_no;
                const currentFormValues = form.getFieldsValue();
                const newFormValues = {
                    ...currentFormValues,
                    invoice_return_no: newInvoiceReturnNo,
                    invoice_no: record.invoice_no,
                    invoice_date: record.invoice_date,
                    sales_order_no: record.sales_order_no,
                    salesman: record.sales_man,
                    packing_list_no: record.packing_list_no,
                    brand: brandDisplayValue,
                    profit_center: record.profit_center,
                    warehouse: record.dealer_re_code,
                    transaction_type: record.transaction_type,
                    event_no: displayEventNo,
                    customer_name: record.customer_name,
                    bill_to: record.name_bill,
                };

                form.setFieldsValue(newFormValues);
                setDataSource([]);
                handleCancel();
                setIsInvoiceSelected(true);
                setAutosaveStatus('idle');
                saveData(newFormValues, []);
            } catch (error) {
                console.error("Failed to generate invoice return number:", error);
                message.error("Gagal membuat nomor Invoice Return. Silakan coba lagi.");
                const displayEventNoError = record.event_no === '01' ? '3rd Invoice Part & Acc' : record.event_no;
                form.setFieldsValue({
                    invoice_return_no: 'Gagal membuat nomor',
                    invoice_no: record.invoice_no,
                    invoice_date: record.invoice_date,
                    sales_order_no: record.sales_order_no,
                    salesman: record.sales_man,
                    packing_list_no: record.packing_list_no,
                    brand: record.brand,
                    profit_center: record.profit_center,
                    warehouse: record.dealer_re_code,
                    transaction_type: record.transaction_type,
                    event_no: displayEventNoError,
                    customer_name: record.customer_name,
                    bill_to: record.name_bill,
                });
                setDataSource([]);
                handleCancel();
                setAutosaveStatus('error');
            }
        };

        const handleRowDoubleClick = async (record: any) => {
            try {
                const checkResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/check-invoice-return-exists/${record.invoice_no}`);
                if (checkResponse.data.exists) {
                    Modal.confirm({
                        title: 'Konfirmasi',
                        content: `Nomor Invoice (${record.invoice_no}) sudah ada dalam daftar. Apakah Anda ingin membuat invoice return lagi?`,
                        okText: 'Ya',
                        cancelText: 'Tidak',
                        onOk: async () => {
                            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/generate-invoice-return-no`);
                            const newInvoiceReturnNo = response.data.invoice_return_no;
                            setOriginalBrandCode(record.brand);
                            setOriginalWarehouseCode(record.dealer_re_code);

                            let brandDisplayValue = record.brand;
                            try {
                                if (record.brand) {
                                    const brandResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/brand-description/${record.brand}`);
                                    if (brandResponse.data && brandResponse.data.description) {
                                        brandDisplayValue = brandResponse.data.description;
                                    }
                                }
                            } catch (brandError) {
                                console.error("Tidak dapat mengambil deskripsi brand, menggunakan kode sebagai gantinya:", brandError);
                            }

                            let warehouseDisplayValue = record.dealer_re_code;
                            try {
                                if (record.dealer_re_code) {
                                    const warehouseResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse-name/${record.dealer_re_code}`);
                                    if (warehouseResponse.data && warehouseResponse.data.warehouse_name) {
                                        warehouseDisplayValue = warehouseResponse.data.warehouse_name;
                                    }
                                }
                            } catch (warehouseError) {
                                console.error("Tidak dapat mengambil nama gudang, menggunakan kode sebagai gantinya:", warehouseError);
                            }

                            const displayEventNo = record.event_no === '01' ? '3rd Invoice Part & Acc' : record.event_no;
                            const currentFormValues = form.getFieldsValue();
                            const newFormValues = {
                                ...currentFormValues,
                                invoice_return_no: newInvoiceReturnNo,
                                invoice_no: record.invoice_no,
                                invoice_date: record.invoice_date,
                                sales_order_no: record.sales_order_no,
                                salesman: record.sales_man,
                                packing_list_no: record.packing_list_no,
                                brand: brandDisplayValue,
                                profit_center: record.profit_center,
                                warehouse: warehouseDisplayValue,
                                transaction_type: record.transaction_type,
                                event_no: displayEventNo,
                                customer_name: record.customer_name,
                                bill_to: record.name_bill,
                            };

                            form.setFieldsValue(newFormValues);
                            setDataSource([]);
                            handleCancel();
                            setIsInvoiceSelected(true);
                            setAutosaveStatus('idle');
                            saveData(newFormValues, []);
                        },
                    });
                } else {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/generate-invoice-return-no`);
                    const newInvoiceReturnNo = response.data.invoice_return_no;
                    setOriginalBrandCode(record.brand);
                    setOriginalWarehouseCode(record.dealer_re_code);

                    let brandDisplayValue = record.brand;
                    try {
                        if (record.brand) {
                            const brandResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/brand-description/${record.brand}`);
                            if (brandResponse.data && brandResponse.data.description) {
                                brandDisplayValue = brandResponse.data.description;
                            }
                        }
                    } catch (brandError) {
                        console.error("Tidak dapat mengambil deskripsi brand, menggunakan kode sebagai gantinya:", brandError);
                    }

                    let warehouseDisplayValue = record.dealer_re_code;
                    try {
                        if (record.dealer_re_code) {
                            const warehouseResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse-name/${record.dealer_re_code}`);
                            if (warehouseResponse.data && warehouseResponse.data.warehouse_name) {
                                warehouseDisplayValue = warehouseResponse.data.warehouse_name;
                            }
                        }
                    } catch (warehouseError) {
                        console.error("Tidak dapat mengambil nama gudang, menggunakan kode sebagai gantinya:", warehouseError);
                    }

                    const displayEventNo = record.event_no === '01' ? '3rd Invoice Part & Acc' : record.event_no;
                    const currentFormValues = form.getFieldsValue();
                    const newFormValues = {
                        ...currentFormValues,
                        invoice_return_no: newInvoiceReturnNo,
                        invoice_no: record.invoice_no,
                        invoice_date: record.invoice_date,
                        sales_order_no: record.sales_order_no,
                        salesman: record.sales_man,
                        packing_list_no: record.packing_list_no,
                        brand: brandDisplayValue,
                        profit_center: record.profit_center,
                        warehouse: warehouseDisplayValue,
                        transaction_type: record.transaction_type,
                        event_no: displayEventNo,
                        customer_name: record.customer_name,
                        bill_to: record.name_bill,
                    };

                    form.setFieldsValue(newFormValues);
                    setDataSource([]);
                    handleCancel();
                    setIsInvoiceSelected(true);
                    setAutosaveStatus('idle');
                    saveData(newFormValues, []);
                }
            } catch (error) {
                console.error("Failed to check for existing invoice return:", error);
                message.error("Gagal memeriksa data invoice. Silakan coba lagi.");
                const displayEventNoError = record.event_no === '01' ? '3rd Invoice Part & Acc' : record.event_no;
                form.setFieldsValue({
                    invoice_return_no: 'Gagal membuat nomor',
                    invoice_no: record.invoice_no,
                    invoice_date: record.invoice_date,
                    sales_order_no: record.sales_order_no,
                    salesman: record.sales_man,
                    packing_list_no: record.packing_list_no,
                    brand: record.brand,
                    profit_center: record.profit_center,
                    warehouse: record.dealer_re_code,
                    transaction_type: record.transaction_type,
                    event_no: displayEventNoError,
                    customer_name: record.customer_name,
                    bill_to: record.name_bill,
                });
                setDataSource([]);
                handleCancel();
                setAutosaveStatus('error');
            }
        };

        const transformStyle = {
            transform: "scale(0.80)",
            transformOrigin: "top left",
            width: "125%",
        };

        const onDateChange: DatePickerProps['onChange'] = (date, dateString) => {
            console.log("Selected Date: ", dateString);
        };

        const columns: TableColumnsType<DataType> = [
            { title: "Item No", dataIndex: "item_code" },
            { title: "Item Name", dataIndex: "item_name" },
            { title: "UoM", dataIndex: "uom" },
            { title: "Qty", dataIndex: "qty" },
            {
                title: "Price",
                dataIndex: "price",
                render: (text: number) => Number(text).toLocaleString('id-ID')
            },
            { title: "Disc %", dataIndex: "disc_percent" },
            {
                title: "Discount",
                dataIndex: "disc_amount",
                render: (text: number) => Number(text).toLocaleString('id-ID')
            },
            {
                title: "Total", dataIndex: "total",
                render: (text: number) => Number(text).toLocaleString('id-ID')
            },
            {
                title: <span style={{ color: 'red' }}>Return</span>,
                dataIndex: "return_qty",
                render: (text: number, record: DataType) => (
                    <InputNumber
                        min={0}
                        max={record.qty}
                        value={text}
                        onChange={(val) => handleReturnQtyChange(val || 0, record.key)}
                    />
                ),
            },
        ];

        const rowSelection: TableProps<DataType>["rowSelection"] = {
            selectedRowKeys,
            onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
        };

        const status = form.getFieldValue('status');
        const invoiceReturnNo = form.getFieldValue('invoice_return_no');
        const isVoidDisabled = status !== 'Draft' || !invoiceReturnNo;
        const isSubmitDisabled = status !== 'Draft' || !invoiceReturnNo
        const isPrintDisabled = !invoiceReturnNo || (status !== 'Approved' && status !== 'Draft' && status !== '1');

        const logicProps: SalesOrderInvoiceReturnProps = {
            transformStyle,
            onDateChange,
            columns,
            rowSelection,
            form,
            isModalOpen,
            showModal,
            handleCancel,
            handleRowDoubleClick,
            isAddLineModalOpen,
            showAddLineModal,
            handleAddLineCancel,
            currentInvoiceNo,
            dataSource,
            handleImport,
            handleDelete,
            selectedRowKeys,
            autosaveStatus,
            handleVoid,
            isVoidDisabled,
            isInvoiceSelected,
            handlePrint,
            isPrintDisabled,
            handleSubmit,
            isSubmitDisabled,
            isReportModalOpen,
            handleReportModalCancel

        };

        return <WrappedComponent {...(props as P)} {...logicProps} />;
    };

    return EnhancedComponent;
};

export default withSalesOrderInvoiceReturnLogic;